import { forwardRef, memo } from 'react';
import { clsx } from 'clsx';
import { Typography } from '../Typography';
import { Box } from '../Box';
import { slowCn } from '../../lib/utils';
import type { PillProps } from './types';
import { decoratorVariants, dotVariants, labelVariants, pillVariants } from './styles';

function Dot({ color, variant }: PillProps) {
  return <Box className={dotVariants({ color, variant })} />;
}

const getLabelVariant = (_size: 'md' | 'sm' | 'xs' | undefined) => {
  switch (_size) {
    case 'md':
      return 'text-sm';
    case 'sm':
      return 'text-xs';
    case 'xs':
    default:
      return 'text-xxxs';
  }
};

const Pill = forwardRef<HTMLDivElement, PillProps>(function Pill(props, ref) {
  const {
    className,
    startDecoratorComponent: StartDecoratorComponent,
    endDecoratorComponent: EndDecoratorComponent,
    startDecoratorNode,
    endDecoratorNode,
    variant = 'clear',
    shape = 'square',
    color = 'gray',
    label,
    isDot,
    pillClassName,
    labelClassName,
    labelTypography,
    disabled,
    size = 'sm',
    ...rest
  } = props;

  const pillClassNames = pillVariants({ variant, color, className, shape, disabled, size });
  const labelClassNames = labelVariants({ variant, color, disabled });
  const decoratorClassNames = decoratorVariants({ color, size, variant });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent className={decoratorClassNames} />
  ) : (
    startDecoratorNode
  );

  const endDecorator = EndDecoratorComponent ? (
    <EndDecoratorComponent className={decoratorClassNames} />
  ) : (
    endDecoratorNode
  );

  if (!label) {
    return (
      <Box
        className={clsx(pillClassNames, labelClassNames, size !== 'md' ? '!p-sm' : '')}
        ref={ref}
        {...rest}
      >
        {isDot ? <Dot {...props} /> : startDecorator}
      </Box>
    );
  }

  return (
    <Box
      className={slowCn(
        pillClassNames,
        labelClassNames,
        { 'cursor-pointer': Boolean(props.onClick) },
        { 'gap-x-xs': isDot },
        { 'gap-x-xs': Boolean(endDecorator) },
        { 'gap-x-xs ps-sm': Boolean(startDecorator) },
        pillClassName,
      )}
      ref={ref}
      {...rest}
    >
      {isDot ? <Dot {...props} /> : startDecorator}
      <Typography
        as="p"
        className={clsx(labelClassNames, labelClassName)}
        maxNumberOfLines={labelTypography?.maxNumberOfLines ?? 1}
        variant={labelTypography?.variant ?? getLabelVariant(size)}
        weight={labelTypography?.weight ?? 'medium'}
      >
        {label}
      </Typography>
      {endDecorator}
    </Box>
  );
});

Pill.displayName = 'Pill';

const MemoizedPill = memo(Pill) as typeof Pill;
export default MemoizedPill;
